import { Trans } from "@lingui/react/macro"
import { useMemo } from "react"
import { resolveSpeakerColor } from "../../../../lib/speaker-colors"
import { talkPercent } from "../../helpers"
import classes from "../../styles.module.css"
import { formatTalkShare } from "./format-talk-share"
import type { IProps } from "./interfaces"

/**
 * TalkTimeCard renders a stacked horizontal bar of per-speaker talk-share
 * percentages followed by a sorted legend with each speaker's share and time.
 *
 * Uses the same `resolveSpeakerColor()` palette as the player legend so a band on
 * the waveform reads as the same person here.
 */
export function TalkTimeCard({ speakerNames, talkShare, speakerColorMap }: IProps): React.JSX.Element | null {
  const talk = useMemo(() => talkPercent(speakerNames, talkShare), [speakerNames, talkShare])
  // Seconds per name, summed the same way as talkPercent, so the label is the
  // time they actually spoke — not a rounded share of the meeting length.
  const secondsByName = useMemo(() => {
    const map = new Map<string, number>()
    if (!talkShare) return map
    speakerNames.forEach((name, i) => {
      map.set(name, (map.get(name) ?? 0) + (talkShare[i] ?? 0))
    })
    return map
  }, [speakerNames, talkShare])
  if (talk.size === 0) return null
  // Dedupe by name: `talk` sums percentages per name, so a person split across
  // more than one roster slot (a merged far-end echo) shows as one legend row.
  const list = [...new Set(speakerNames)]
    .map((name) => ({ name, pct: talk.get(name) ?? 0 }))
    .filter((x) => x.pct > 0)
    .sort((a, b) => b.pct - a.pct)

  return (
    <section className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <Trans>Talk-share</Trans>
        </h2>
      </div>
      <div className={classes.speakersBar}>
        {list.map((p, i) => (
          <div
            key={`${i}-${p.name}`}
            className={classes.speakersBarSeg}
            style={{ width: `${p.pct}%`, background: resolveSpeakerColor(p.name, speakerColorMap) }}
            title={`${p.name} — ${p.pct}%`}
          />
        ))}
      </div>
      <div className={classes.speakersList}>
        {list.map((p, i) => (
          <div key={`${i}-${p.name}`} className={classes.speakersRow}>
            <span
              className={classes.speakersDot}
              style={{ background: resolveSpeakerColor(p.name, speakerColorMap) }}
            />
            <span className={classes.speakersName}>{p.name}</span>
            <span className={classes.speakersPct}>{p.pct}%</span>
            <span className={classes.speakersTime}>{formatTalkShare(secondsByName.get(p.name) ?? 0)}</span>
          </div>
        ))}
      </div>
    </section>
  )
}
